import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { logUserToMixpanel } from "../utils/mixpanel";
import { useAPI } from "../hooks/useAPI";
import { checkConnectionQuality } from "../services/connectionTest";

const AuthContext = createContext(null);

export const AuthProvider = ({ children, enabled = true }) => {
  const queryClient = useQueryClient();
  const { callAPI } = useAPI();

  // 1. Initialize Token Lazy (Avoids effect flash)
  const [authToken, setAuthToken] = useState(() => {
    return Cookies.get("token") || "";
  });

  // Helper to determine 2FA status
  const is2FA =
    typeof window !== "undefined" &&
    window.location.pathname.toLowerCase().includes("2fa");

  // 2. Define the Query for Fetching User
  const {
    data: loggedInUser = {},
    isLoading,
    error,
    refetch: fetchUser,
  } = useQuery({
    queryKey: ["user", authToken],
    queryFn: async ({ signal }) => {
      const userData = await callAPI(`user`, { token: authToken });

      if (!Object.keys(userData).includes("email")) {
        throw new Error("Invalid user");
      }
      return userData;
    },
    // Only run if we have a token, we aren't in 2FA, and the hook is enabled
    enabled: !!authToken && !is2FA && enabled,
    retry: false, // Don't retry if auth fails (usually means 401)
    staleTime: 1000 * 60 * 5, // Cache user data for 5 minutes
  });

  // 3. Side Effect: Logging to Mixpanel
  useEffect(() => {
    if (loggedInUser && loggedInUser.email) {
      logUserToMixpanel(loggedInUser);
    }
  }, [loggedInUser]);

  // 4. Connection quality check — runs once when the user is authenticated.
  // Skipped on auth-flow pages (/2fa_*, /login) because saveAuthToken fires the
  // authToken change there, and the test's in-flight /test_connection would be
  // aborted by the imminent window.location.href reload to /dashboard/home.
  useEffect(() => {
    if (!authToken) return;
    const path = window.location.pathname.toLowerCase();
    if (path.includes("2fa") || path.startsWith("/login")) return;
    checkConnectionQuality(callAPI, authToken).catch(() => {});
  }, [authToken]);

  // 5. Save Token Helper
  const saveAuthToken = useCallback(
    (token, rememberMe = false) => {
      const isSecure = window.location.protocol === "https:";

      Cookies.set("token", token, {
        ...(rememberMe ? { expires: 30 } : {}),
        secure: isSecure,
        sameSite: isSecure ? "None" : "Lax",
      });

      setAuthToken(token);

      // Invalidate queries so UI updates immediately if switching users
      queryClient.invalidateQueries(["user"]);
    },
    [queryClient]
  );

  // Optional: Add a logout helper
  const logout = useCallback(() => {
    Cookies.remove("token");
    setAuthToken("");
    queryClient.setQueryData(["user", authToken], {});
    queryClient.clear();
    // Remove the persisted React Query cache so stale data isn't restored on
    // the next login (key is set in main.jsx PersistQueryClientProvider).
    localStorage.removeItem("GURUNOW_REACT_QUERY_CACHE");
    localStorage.removeItem("gn_conn_test");
    sessionStorage.clear();
  }, [queryClient, authToken]);



  const value = {
    authToken,
    saveAuthToken,
    loggedInUser,
    fetchUser,
    isLoading,
    error,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom Hook to consume the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
